import type { PromptUse } from '@/components/features/Editor';
import { stripVoiceNoteMarker } from '@/lib/utils';
import api, { handleError } from '@/services/axiosInstance';
import {
  CHAT_MESSAGE_RATING,
  CHAT_USER_ROLE,
  LibraryReference,
  Session,
} from '@/types/chat';
import { parseLibraryReferences } from '@/utils/libraryReferences';

export enum SESSION_STATUS {
  OPEN = 'open',
  CLOSED = 'closed',
  PENDING = 'pending',
}

export interface MessageRequest {
  message: string;
  jobId?: string;
  contactId?: string;
  doc_id?: string;
  quote_id?: string;
  session_id?: string;
  /**
   * Document and quote chats: the Prompt Templates record of the message's chips
   * (FSI #1235). Xano keeps it as Prompt Use rows; these chats never reach FSI, so
   * their history has no record. Sent only when non-empty.
   */
  prompts?: PromptUse[];
}

export interface MessageResponse {
  message: string;
  message_id: string;
  session_id: string;
  tools?: string[];
}

export interface SessionHistoryResponse {
  session_id: string;
  status: SESSION_STATUS;
  message_count: number;
  messages: {
    id: string;
    created_at: number;
    user: number;
    role: CHAT_USER_ROLE;
    body: string;
    agent?: {
      name: string;
      avatar: {
        url: string;
      };
    };
    agents_id?: number;
    is_positive: boolean;
    is_negative: boolean;
    tools?: string[];
    /** Connie only: FSI's turn index, for citation excerpts (FSI #1159). */
    turnIndex?: number;
    /** Connie only: Library Documents the turn touched. */
    libraryReferences?: LibraryReference[];
    /** Connie only, user messages: the Prompt Templates record (FSI #1234). */
    prompts?: PromptUse[];
  }[];
}

export interface MessageFeedbackRequest {
  message_id: string;
  rating: CHAT_MESSAGE_RATING;
}

export interface MessageFeedbackFormRequest {
  message_id: string;
  feedback: string;
  session_id: string;
  reasons?: string[];
}

const API_URL = 'https://api.vh3connect.io';
const CONNIE_API_URL = 'https://api.vh3connect.io/api:f-Fs4_ed';

type ConnieSessionResponse = {
  sessions?: unknown[];
};

type ConnieSessionHistoryResponse = {
  session_id?: string;
  sessionId?: string;
  status?: SESSION_STATUS;
  messages?: unknown[];
};

type SessionHistoryMessage = SessionHistoryResponse['messages'][number];

type ConnieMessageResponse = {
  message?: string;
  response?: string;
  content?: string;
  message_id?: string;
  messageId?: string;
  session_id?: string;
  sessionId?: string;
  toolCalls?: unknown;
  toolCallOutputs?: unknown;
  toolUsed?: unknown;
};

const toTimestamp = (value: unknown): number => {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  return 0;
};

const toToolNames = (value: unknown): string[] => {
  if (typeof value === 'string') {
    return value
      .split(',')
      .map(tool => tool.trim())
      .filter(Boolean);
  }

  if (!Array.isArray(value)) {
    if (value && typeof value === 'object') {
      const record = value as Record<string, unknown>;
      const toolName = record.name ?? record.toolName ?? record.tool;
      return toolName ? [String(toolName)] : [];
    }

    return [];
  }

  return value
    .map(tool => {
      if (typeof tool === 'string') {
        return tool;
      }

      if (tool && typeof tool === 'object') {
        const record = tool as Record<string, unknown>;
        return String(record.name ?? record.toolName ?? record.tool ?? '');
      }

      return '';
    })
    .filter(Boolean);
};

/** Tia sessions restore only through the Tia doors, never Connie's history. */
const isTiaSession = (value: unknown) => {
  const session = value as Record<string, unknown> | null;
  return (
    session?.agentVariant === 'tia' ||
    session?.entityType === 'library_document'
  );
};

const toConnieSession = (value: unknown): Session => {
  const session = value as Record<string, unknown>;
  const agent = session._agent ?? session.agent;
  const agentRecord = agent as Record<string, unknown> | undefined;
  const avatar = agentRecord?.avatar as Record<string, unknown> | undefined;
  const senderType =
    session.last_message_sender_type ?? session.lastMessageSenderType;

  const rawTitle = stripVoiceNoteMarker(String(session.title ?? '')).trim();
  const preview = stripVoiceNoteMarker(String(session.preview ?? '')).trim();
  const description = String(session.description ?? '').trim();
  const turnCount =
    typeof session.turnCount === 'number'
      ? session.turnCount
      : Number(session.turnCount ?? 0) || 0;
  const lastActivity = toTimestamp(
    session.last_message_timestamp ??
      session.lastMessageTimestamp ??
      session.lastActivity ??
      session.updated_at ??
      session.updatedAt
  );
  // Only treat as pending when activity is fresh — older untitled rows have no backfill
  const TITLE_PENDING_WINDOW_MS = 30_000;
  const titlePending =
    !rawTitle &&
    !!preview &&
    lastActivity > 0 &&
    Date.now() - lastActivity < TITLE_PENDING_WINDOW_MS;

  // primary = title || preview || turn-count fallback — never invent turn-count when title/preview exist
  const title = rawTitle || preview || `Connie chat - ${turnCount} turns`;

  return {
    id: String(session.id ?? session.session_id ?? session.sessionId ?? ''),
    created_at: toTimestamp(session.created_at ?? session.createdAt),
    user: typeof session.user === 'number' ? session.user : undefined,
    agent: typeof session.agent === 'number' ? session.agent : undefined,
    title,
    // secondary subtitle only — omit when empty (do not invent "N turns")
    description,
    updated_at: toTimestamp(
      session.updated_at ?? session.updatedAt ?? session.lastActivity
    ),
    BCjobID: '',
    is_archived: Boolean(session.is_archived ?? session.isArchived),
    status:
      (session.status as SESSION_STATUS | undefined) ?? SESSION_STATUS.OPEN,
    // List API has no last message body; avoid fake "N turns" grey line
    last_message: String(
      session.last_message ?? session.lastMessage ?? ''
    ).trim(),
    last_message_sender_type:
      senderType === CHAT_USER_ROLE.USER || senderType === 'human'
        ? CHAT_USER_ROLE.USER
        : CHAT_USER_ROLE.AGENT,
    last_message_timestamp: lastActivity,
    turnCount,
    titlePending,
    _agent: {
      name: String(agentRecord?.name ?? 'Connie'),
      avatar: {
        url: String(avatar?.url ?? ''),
      },
    },
  };
};

const toConnieSessionHistory = (
  data: ConnieSessionHistoryResponse,
  sessionId: string
): SessionHistoryResponse => ({
  session_id: data.session_id ?? data.sessionId ?? sessionId,
  status: data.status ?? SESSION_STATUS.OPEN,
  message_count: data.messages?.length ?? 0,
  messages: (data.messages ?? []).flatMap<SessionHistoryMessage>(
    (value, index) => {
      const message = value as Record<string, unknown>;
      const agent = message.agent as Record<string, unknown> | undefined;
      const avatar = agent?.avatar as Record<string, unknown> | undefined;
      const role = message.role;
      const messageId = String(
        message.id ?? message.message_id ?? message.messageId ?? index
      );
      const createdAt = toTimestamp(message.created_at ?? message.createdAt);
      const userMessage =
        typeof message.userMessage === 'string'
          ? stripVoiceNoteMarker(message.userMessage)
          : '';
      const assistantMessage =
        typeof message.assistantMessage === 'string'
          ? message.assistantMessage
          : '';

      if (userMessage || assistantMessage) {
        return [
          ...(userMessage
            ? [
                {
                  id: `${messageId}-user`,
                  created_at: createdAt,
                  user: typeof message.userId === 'number' ? message.userId : 0,
                  role: CHAT_USER_ROLE.USER,
                  body: userMessage,
                  is_positive: false,
                  is_negative: false,
                  // The bubble checks each range against the text before drawing it.
                  ...(Array.isArray(message.prompts) &&
                    message.prompts.length > 0 && {
                      prompts: message.prompts as PromptUse[],
                    }),
                },
              ]
            : []),
          ...(assistantMessage
            ? [
                {
                  id: `${messageId}-assistant`,
                  created_at: createdAt,
                  user: 0,
                  role: CHAT_USER_ROLE.AGENT,
                  body: assistantMessage,
                  agent: {
                    name: 'Connie',
                    avatar: { url: '' },
                  },
                  is_positive: false,
                  is_negative: false,
                  tools: toToolNames(message.toolUsed),
                  turnIndex:
                    typeof message.turnIndex === 'number'
                      ? message.turnIndex
                      : undefined,
                  libraryReferences: parseLibraryReferences(
                    message.libraryReferences
                  ),
                },
              ]
            : []),
        ];
      }

      return {
        id: messageId,
        created_at: createdAt,
        user: typeof message.user === 'number' ? message.user : 0,
        role:
          role === CHAT_USER_ROLE.USER || role === 'human'
            ? CHAT_USER_ROLE.USER
            : CHAT_USER_ROLE.AGENT,
        body: String(message.body ?? message.content ?? message.message ?? ''),
        agent: agent
          ? {
              name: String(agent.name ?? 'Connie'),
              avatar: { url: String(avatar?.url ?? '') },
            }
          : undefined,
        agents_id:
          typeof message.agents_id === 'number' ? message.agents_id : undefined,
        is_positive: Boolean(message.is_positive),
        is_negative: Boolean(message.is_negative),
        tools: toToolNames(
          message.toolUsed ?? message.toolCalls ?? message.toolCallOutputs
        ),
      };
    }
  ),
});

export const sessionApi = {
  getConnieSessions: async ({
    user_id,
    contact_id,
  }: {
    user_id: string;
    contact_id?: string;
  }): Promise<Session[] | undefined> => {
    try {
      const { data } = await api.get<ConnieSessionResponse>(
        `${CONNIE_API_URL}/connie/sessions`,
        {
          params: {
            user_id,
            ...(contact_id ? { contact_id } : {}),
          },
        }
      );
      return (data.sessions ?? [])
        .filter(session => !isTiaSession(session))
        .map(toConnieSession);
    } catch (error) {
      handleError(error, 'getConnieSessions');
    }
  },

  getConnieSessionHistory: async (
    sessionId: string
  ): Promise<SessionHistoryResponse | undefined> => {
    try {
      const { data } = await api.get<ConnieSessionHistoryResponse>(
        `${CONNIE_API_URL}/connie/sessions/${sessionId}/messages`
      );
      return toConnieSessionHistory(data, sessionId);
    } catch (error) {
      handleError(error, 'getConnieSessionHistory');
    }
  },

  getSessionHistory: async (
    sessionId: string
  ): Promise<SessionHistoryResponse | undefined> => {
    try {
      const { data } = await api.get<SessionHistoryResponse>(
        `${API_URL}/api:suR6dssJ/session/${sessionId}`
      );
      return data;
    } catch (error) {
      handleError(error, 'getSessionHistory');
    }
  },

  getDocumentSessionHistory: async (
    sessionId: string
  ): Promise<SessionHistoryResponse | undefined> => {
    try {
      const { data } = await api.get<SessionHistoryResponse>(
        `${API_URL}/api:uCamsOiN/documents/chat/session/${sessionId}`
      );
      return data;
    } catch (error) {
      handleError(error, 'getDocumentSessionHistory');
    }
  },

  getDocumentSessions: async ({
    doc_id,
  }: {
    doc_id: string;
  }): Promise<Session[] | undefined> => {
    try {
      const searchParams = new URLSearchParams();
      searchParams.append('doc_id', doc_id);

      const { data } = await api.get<Session[]>(
        `${API_URL}/api:uCamsOiN/documents/chat/list/sessions?${searchParams}`
      );
      return data;
    } catch (error) {
      handleError(error, 'getDocumentSessions');
    }
  },

  getContactSessions: async ({
    contact_id,
  }: {
    contact_id: string;
  }): Promise<Session[] | undefined> => {
    try {
      const searchParams = new URLSearchParams();
      searchParams.append('contact_id', contact_id);

      const { data } = await api.get<Session[]>(
        `${API_URL}/api:suR6dssJ/session?${searchParams}`
      );
      return data;
    } catch (error) {
      handleError(error, 'getContactSessions');
    }
  },

  getQuoteSessions: async ({
    quote_id,
  }: {
    quote_id: string;
  }): Promise<Session[] | undefined> => {
    try {
      const searchParams = new URLSearchParams();
      searchParams.append('quote_id', quote_id);

      const { data } = await api.get<Session[]>(
        `${API_URL}/api:uCamsOiN/quotes/chat/list/sessions?${searchParams}`
      );

      return data;
    } catch (error) {
      handleError(error, 'getQuoteSessions');
    }
  },

  sendMessage: async (
    sendData: MessageRequest
  ): Promise<MessageResponse | undefined> => {
    try {
      const { data } = await api.post<MessageResponse>(
        `${API_URL}/api:sLAMGphr/chat/job_history/sendMessage/v2`,
        sendData
      );

      if (!data.message_id || !data.session_id) {
        throw new Error('Invalid session response');
      }

      return data;
    } catch (error) {
      handleError(error, 'sendMessage');
    }
  },

  sendConnieMessage: async (
    sendData: MessageRequest
  ): Promise<MessageResponse | undefined> => {
    try {
      const { data } = await api.post<ConnieMessageResponse>(
        `${CONNIE_API_URL}/connie/chat`,
        sendData
      );
      const message = data.message ?? data.response ?? data.content;
      const messageId =
        data.message_id ?? data.messageId ?? crypto.randomUUID();
      const sessionId = data.session_id ?? data.sessionId;

      if (!message || !sessionId) {
        throw new Error('Invalid Connie chat response');
      }

      return {
        message,
        message_id: messageId,
        session_id: sessionId,
        tools: toToolNames(
          data.toolCalls ?? data.toolCallOutputs ?? data.toolUsed
        ),
      };
    } catch (error) {
      handleError(error, 'sendConnieMessage');
    }
  },

  sendDocumentMessage: async (
    sendData: MessageRequest
  ): Promise<MessageResponse | undefined> => {
    try {
      const { data } = await api.post<MessageResponse>(
        sendData.doc_id
          ? `${API_URL}/api:uCamsOiN/documents/chat/sendMessage`
          : `${API_URL}/api:sLAMGphr/chat/document/sendMessage`,
        sendData
      );

      if (!data.message_id || !data.session_id) {
        throw new Error('Invalid session response');
      }

      return data;
    } catch (error) {
      handleError(error, 'sendDocumentMessage');
    }
  },

  sendQuoteMessage: async (
    sendData: MessageRequest
  ): Promise<MessageResponse | undefined> => {
    try {
      const { data } = await api.post<MessageResponse>(
        `${API_URL}/api:uCamsOiN/quotes/chat/sendMessage`,
        sendData
      );

      if (!data.message_id || !data.session_id) {
        throw new Error('Invalid session response');
      }

      return data;
    } catch (error) {
      handleError(error, 'sendQuoteMessage');
    }
  },

  sendFeedbackMessage: async (
    sendData: MessageFeedbackRequest
  ): Promise<void> => {
    try {
      await api.patch(
        `${API_URL}/api:suR6dssJ/chat_message/${sendData.message_id}/feedback`,
        sendData
      );
    } catch (error) {
      handleError(error, 'sendFeedbackMessage');
    }
  },

  sendFeedback: async (data: MessageFeedbackFormRequest): Promise<void> => {
    try {
      await api.post(`${API_URL}/api:6q5N0phZ/feedback`, data);
    } catch (error) {
      handleError(error, 'sendFeedback');
    }
  },
};
